import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/useAuthStore";
import type { Notification } from "@/lib/types";
import { feedCelebrations } from "@/features/celebrations/feedCelebrations";

/**
 * Fetches notifications via polling.
 * Returns notifications and unread count with periodic updates.
 *
 * NOTE: Previously used Supabase Realtime postgres_changes for instant delivery,
 * but Privy token-exchange auth causes WebSocket reconnect storms.
 * Polling at 10s is a reliable fallback.
 *
 * Not React Query, so the cache clear on a user switch does not reach it: the
 * list is local state, and the effect below owns its per-user reset.
 */
export function useNotifications() {
  const userId = useAuthStore((s) => s.session?.userId);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch + poll for notifications. Everything here is scoped to ONE userId:
  // a Privy account switch arrives as A → B with no null in between, so the
  // previous account's list is dropped up front, and a fetch still in flight
  // for A is discarded when it lands rather than written over B's list (or
  // fed to B's celebrations).
  useEffect(() => {
    setNotifications([]);
    if (!userId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    let cancelled = false;
    let fetching = false;
    const fetchNotifications = async () => {
      if (fetching) return;
      fetching = true;
      try {
        const { data, error } = await supabase
          .from("notifications")
          .select("*")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(50);

        if (cancelled) return;
        if (error) {
          console.error("[useNotifications] Fetch error:", error);
        } else {
          const mapped = (data ?? []).map((row) => ({
            id: row.id,
            userId: row.user_id,
            type: row.type,
            title: row.title,
            message: row.message,
            linkTo: row.link_to,
            read: row.read,
            createdAt: row.created_at,
          }));
          setNotifications(mapped);
          // Counterparty celebrations ride this poll — no request of their own.
          feedCelebrations(userId, mapped);
        }
      } finally {
        fetching = false;
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 10_000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [userId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = useCallback(
    async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", notificationId);

      if (error) {
        console.error("[useNotifications] markAsRead failed:", error);
        return;
      }

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read: true } : n,
        ),
      );
    },
    [],
  );

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", userId)
      .eq("read", false);

    if (error) {
      console.error("[useNotifications] markAllAsRead failed:", error);
      return;
    }

    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, [userId]);

  return { notifications, unreadCount, isLoading, markAsRead, markAllAsRead };
}
